import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";

/**
 * Generic ledger page for fines / incentives / advances.
 * Pass: title, accent, endpoint, categories
 */
export default function Ledger({ title, accent = "#E50914", endpoint, categories, isAdvance = false }) {
  const [rows, setRows] = useState([]);
  const [emps, setEmps] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});

  async function load() {
    try {
      const [a, e] = await Promise.all([api.get(`/${endpoint}`), api.get("/employees")]);
      setRows(a.data); setEmps(e.data);
    } catch (e2) { toast.error(formatApiError(e2)); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);

  function openNew() {
    setForm(isAdvance
      ? { employee_id: "", advance_type: "ADVANCE", amount: 0, installments: 1, reason: "" }
      : { employee_id: "", category: categories[0]?.value || "OTHER", description: "", amount: 0 });
    setOpen(true);
  }

  async function save() {
    try {
      await api.post(`/${endpoint}`, form);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function remove(id) {
    if (!window.confirm("Delete?")) return;
    try { await api.delete(`/${endpoint}/${id}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  }

  const total = rows.reduce((s, r) => s + Number(r.amount || 0), 0);
  const empName = (id) => emps.find((e) => e.id === id)?.name || id;

  return (
    <div className="p-6 md:p-10">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase mb-2" style={{color: accent}}>Ledger</p>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight">{title}</h1>
          <p className="text-xs text-zinc-500 mt-1 font-mono">Total: ₹ {total.toLocaleString()}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} data-testid={`add-${endpoint}-button`} className="bg-[#E50914] hover:bg-[#C80812] h-10">
              <Plus size={16} className="mr-1" /> Add
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#16161C] border-[#2D2D35] text-white">
            <DialogHeader><DialogTitle>New {title}</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Employee</Label>
                <Select value={form.employee_id} onValueChange={(v) => setForm({...form, employee_id: v})}>
                  <SelectTrigger data-testid="ledger-emp-select" className="mt-2 bg-[#0D0D10] border-[#2D2D35]"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent className="bg-[#16161C] border-[#2D2D35] text-white max-h-72">
                    {emps.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} · {e.employee_id}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {isAdvance ? (<>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Type</Label>
                  <Select value={form.advance_type} onValueChange={(v) => setForm({...form, advance_type: v})}>
                    <SelectTrigger className="mt-2 bg-[#0D0D10] border-[#2D2D35]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#16161C] border-[#2D2D35] text-white">
                      <SelectItem value="ADVANCE">Salary Advance</SelectItem>
                      <SelectItem value="LOAN">Employee Loan</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs uppercase tracking-widest text-zinc-500">Amount</Label>
                    <Input type="number" value={form.amount} onChange={(e)=>setForm({...form, amount: Number(e.target.value)})}
                           className="mt-2 bg-[#0D0D10] border-[#2D2D35]" data-testid="ledger-amount" /></div>
                  <div><Label className="text-xs uppercase tracking-widest text-zinc-500">Installments</Label>
                    <Input type="number" value={form.installments} onChange={(e)=>setForm({...form, installments: Number(e.target.value)})}
                           className="mt-2 bg-[#0D0D10] border-[#2D2D35]" /></div>
                </div>
                <div><Label className="text-xs uppercase tracking-widest text-zinc-500">Reason</Label>
                  <Textarea value={form.reason || ""} onChange={(e)=>setForm({...form, reason: e.target.value})}
                            className="mt-2 bg-[#0D0D10] border-[#2D2D35]" /></div>
              </>) : (<>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                    <SelectTrigger className="mt-2 bg-[#0D0D10] border-[#2D2D35]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#16161C] border-[#2D2D35] text-white">
                      {categories.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs uppercase tracking-widest text-zinc-500">Description</Label>
                  <Textarea value={form.description || ""} onChange={(e)=>setForm({...form, description: e.target.value})}
                            className="mt-2 bg-[#0D0D10] border-[#2D2D35]" /></div>
                <div><Label className="text-xs uppercase tracking-widest text-zinc-500">Amount (INR)</Label>
                  <Input type="number" value={form.amount} onChange={(e)=>setForm({...form, amount: Number(e.target.value)})}
                         className="mt-2 bg-[#0D0D10] border-[#2D2D35]" data-testid="ledger-amount" /></div>
              </>)}
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={()=>setOpen(false)} className="border-[#2D2D35] bg-transparent">Cancel</Button>
                <Button onClick={save} data-testid="ledger-save" className="bg-[#E50914] hover:bg-[#C80812]">Save</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-[#16161C] border border-[#2D2D35] rounded-md overflow-hidden">
        <table className="w-full" data-testid={`${endpoint}-table`}>
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[#2D2D35]">
              <th className="text-left p-3">Employee</th>
              <th className="text-left p-3">{isAdvance ? "Type" : "Category"}</th>
              <th className="text-left p-3">{isAdvance ? "Reason" : "Description"}</th>
              <th className="text-left p-3">Date</th>
              <th className="text-right p-3">Amount</th>
              {isAdvance && <th className="text-right p-3">Balance</th>}
              <th className="text-right p-3"></th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-[#2D2D35]/60 hover:bg-[#1C1C24]">
                <td className="p-3">{empName(r.employee_id)}</td>
                <td className="p-3 text-xs"><span className="px-2 py-1 rounded bg-[#0D0D10] border border-[#2D2D35]">{r.category || r.advance_type}</span></td>
                <td className="p-3 text-xs text-zinc-300 max-w-md truncate">{r.description || r.reason}</td>
                <td className="p-3 text-xs font-mono text-zinc-400">{r.date?.slice(0,10)}</td>
                <td className="p-3 text-right font-mono">₹ {Number(r.amount||0).toLocaleString()}</td>
                {isAdvance && <td className="p-3 text-right font-mono text-[#F59E0B]">₹ {Number(r.balance||0).toLocaleString()}</td>}
                <td className="p-3 text-right">
                  {!isAdvance && (
                    <button onClick={()=>remove(r.id)} className="p-2 hover:text-[#FF3B30]"><Trash size={16} /></button>
                  )}
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={isAdvance ? 7 : 6} className="text-center text-zinc-500 p-8 text-sm">Nothing recorded.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
